import {
  Checkbox,
  ClearBehaviour,
  DropZone,
  InputButton,
  Select,
  SelectProps,
  SortedFiles,
} from '@infotech-soe/erp-ui-kit';
import {
  DSAgents,
  KeyTypes,
  SignCredentials,
} from '@infotech-soe/mia-ds-service/types';
import { isNumber } from 'lodash';
import React, {
  MutableRefObject,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useIntl } from 'react-intl';

import { readAsUint8Array } from '../../../helpers';
import { AvailableApplication } from '../../../services/auth/types';
import { SelectFormControlEntryType } from '../../../services/form/view-model/FormControlViewModel';
import { useSign } from '../../hooks/useSign';
import ApplicationSelectorContainer from '../session/ApplicationSelectorContainer';
import { DSLoginFormContainerProps } from '../session/AuthFormContainer';
import BaseAuthForm from '../session/BaseAuthForm';
import LoginFormField from '../session/LoginFormField';
import { PasswordInput } from '../session/PasswordInput';
import { useExternalFormStyles } from '../session/styles';

import AgentNotAvailableForm from './AgentNotAvailableForm';
import { FILE_KEY_TYPES } from './constants';
import { dsFormMessages } from './messages';

const DSAgentForm: React.FC<DSLoginFormContainerProps> = ({
  allowedDSMediaTypes,
  applications = [],
  dsAgent = DSAgents.CIPHER,
  dsKeyTypes,
  forbiddenDSMediaTypes,
  hasError,
  signAlgo,
  signatureType,
  signData,
  submitLabel,
  withApplications,
  withTitle,
  onError,
  onSubmit,
}) => {
  const { formatMessage } = useIntl();
  const classes = useExternalFormStyles();

  const [password, setPassword] = useState('');
  const [app, setApp] = useState<AvailableApplication['name']>(() => {
    const stored = localStorage.getItem('app');
    return (
      (applications.some(a => a.name === stored)
        ? stored
        : applications[0]?.name) ?? ''
    );
  });
  const [authority, setAuthority] = useState<string | null>(null);
  const [keyType, setKeyType] = useState<KeyTypes | null>(dsKeyTypes[0]);
  const [keyFile, setKeyFile] = useState<File | null>(null);
  const [currentDeviceType, setCurrentDeviceType] = useState<number | null>(
    null
  );
  const [currentDevice, setCurrentDevice] = useState<number | null>(null);
  const [openDeviceTypeSelector, setOpenDeviceTypeSelector] = useState(false);
  const [saveCredentials, setSaveCredentials] = useState(false);

  const isDevicesRefreshed: MutableRefObject<boolean> = useRef(false);

  const { init, loading, authorities, signDevices, onRefreshDevices, onSign } =
    useSign(
      dsAgent,
      dsKeyTypes,
      allowedDSMediaTypes,
      forbiddenDSMediaTypes,
      onError
    );

  const authorityOptions = authorities.map(item => ({
    type: SelectFormControlEntryType.OPTION,
    value: item[0],
    label: item[1],
  }));

  const keyTypeOptions: SelectProps<KeyTypes>['options'] = useMemo(
    () =>
      dsKeyTypes.map(type => ({
        type: 'listItem',
        label: formatMessage(dsFormMessages[type]),
        value: type,
      })),
    [dsKeyTypes, formatMessage]
  );

  const deviceTypeOptions: SelectProps<number>['options'] = useMemo(
    () =>
      signDevices.map(deviceType => ({
        type: SelectFormControlEntryType.OPTION,
        value: deviceType.index,
        label: deviceType.name,
      })),
    [signDevices]
  );

  const deviceOptions: SelectProps<number>['options'] = useMemo(() => {
    if (!isNumber(currentDeviceType)) return [];
    return (
      signDevices
        .find(kmType => kmType.index === currentDeviceType)
        ?.devices.map(dev => ({
          type: SelectFormControlEntryType.OPTION,
          value: dev.index,
          label: dev.name,
        })) ?? []
    );
  }, [currentDeviceType, signDevices]);

  const enableSubmit = useMemo(
    () =>
      !!password &&
      (dsAgent === DSAgents.IIT || !!authority) &&
      (keyType === KeyTypes.DEVICE
        ? isNumber(currentDeviceType) && isNumber(currentDevice)
        : !!keyFile),
    [
      authority,
      currentDevice,
      currentDeviceType,
      dsAgent,
      keyFile,
      keyType,
      password,
    ]
  );

  const handleDrop = ({ acceptedFiles }: SortedFiles) => {
    setKeyFile(acceptedFiles[0]);
  };

  const handleSubmit = async () => {
    if (enableSubmit) {
      try {
        const keyFileTypedArray = keyFile && (await readAsUint8Array(keyFile));

        const credentials: SignCredentials = {
          dsAgent,
          keyType: keyType ?? KeyTypes.FILE,
          password,
          ...(dsAgent === DSAgents.CIPHER
            ? {
                authority: authority || null,
                devicePath:
                  signDevices
                    .find(type => type.index === currentDeviceType)
                    ?.devices.find(device => device.index === currentDevice)
                    ?.name ?? null,
                keyFile,
              }
            : {
                device: currentDevice,
                deviceType: currentDeviceType,
                keyFile: keyFileTypedArray,
                signAlgo,
              }),
        };

        const signActions = signData?.map(data =>
          onSign(credentials, signatureType, data, saveCredentials)
        );
        if (app) localStorage.setItem('app', app);
        onSubmit(
          {
            signatures: await Promise.all(signActions),
            credentials,
            signatureType,
            saveCredentials,
          },
          app
        );
      } catch (error: any) {
        onError(error);
      }
    }
  };

  const refreshDevices = useCallback(async () => {
    setOpenDeviceTypeSelector(false);
    setCurrentDeviceType(null);
    setCurrentDevice(null);
    await onRefreshDevices();
    setOpenDeviceTypeSelector(true);
  }, [onRefreshDevices]);

  const handleChangeKeyType = useCallback(
    async (kType: KeyTypes | null) => {
      setKeyType(kType);
      if (!isDevicesRefreshed.current) {
        await refreshDevices();
        isDevicesRefreshed.current = true;
      }
    },
    [refreshDevices]
  );

  if (!init && !loading) {
    return <AgentNotAvailableForm dsAgent={dsAgent} />;
  }

  return (
    <BaseAuthForm
      hasError={hasError}
      loading={loading}
      logoSrc={dsAgent === DSAgents.IIT ? '/logo-IIT.png' : '/logo-CIPHER.png'}
      submitLabel={submitLabel ?? formatMessage(dsFormMessages.sign)}
      valid={enableSubmit}
      withChange={false}
      withTitle={withTitle}
      onSubmit={async () => {
        await handleSubmit();
      }}
    >
      {withApplications && (
        <ApplicationSelectorContainer value={app} onChange={setApp} />
      )}
      {dsAgent === DSAgents.CIPHER && (
        <LoginFormField label={formatMessage(dsFormMessages.authority)}>
          <Select
            disabled={loading}
            options={authorityOptions}
            value={authority}
            onChange={setAuthority}
          />
        </LoginFormField>
      )}
      <LoginFormField label={formatMessage(dsFormMessages.keyType)}>
        <Select
          name="keyType"
          disabled={keyTypeOptions.length <= 1}
          value={keyType}
          options={keyTypeOptions}
          withClear={ClearBehaviour.NONE}
          onChange={handleChangeKeyType}
        />
      </LoginFormField>

      {keyType === KeyTypes.DEVICE ? (
        <>
          <LoginFormField label={formatMessage(dsFormMessages.deviceType)}>
            <Select
              options={deviceTypeOptions}
              name="deviceType"
              value={currentDeviceType}
              withClear={ClearBehaviour.NONE}
              onChange={setCurrentDeviceType}
              endSlot={<InputButton icon="refresh" onClick={refreshDevices} />}
              open={openDeviceTypeSelector}
              onOpen={setOpenDeviceTypeSelector}
            />
          </LoginFormField>
          <LoginFormField label={formatMessage(dsFormMessages.device)}>
            <Select
              options={deviceOptions}
              name="device"
              disabled={currentDeviceType === null}
              value={currentDevice}
              withClear={ClearBehaviour.NONE}
              onChange={setCurrentDevice}
            />
          </LoginFormField>
        </>
      ) : (
        <DropZone
          accept={FILE_KEY_TYPES.join(',')}
          className={classes.dropzone}
          name="file"
          onDrop={handleDrop}
        >
          {keyFile && <span>{keyFile.name}</span>}
        </DropZone>
      )}

      <PasswordInput
        disabled={loading}
        label={formatMessage(dsFormMessages.password)}
        value={password}
        onChange={setPassword}
      />
      <Checkbox
        name="saveCredentials"
        label={formatMessage(dsFormMessages.saveCredentials)}
        value={saveCredentials}
        onChange={setSaveCredentials}
      />
    </BaseAuthForm>
  );
};

export default DSAgentForm;
